import { CellData } from '../../../../../../common/table/TableTypes';
import {
    TABLE_DEFAULT_COLORS,
    TABLE_DEFAULT_HEADER_BACKGROUND_COLOR,
} from '../../../../../../common/table/tableConstants';
import { getColorDisplayValue } from '../../../../../../common/colors/elementColorFormatUtil';
import { TiptapNodeType } from '../../../../../../common/tiptap/tiptapTypes';
import type { TiptapContent } from '../../../../../../common/tiptap/tiptapTypes';
import { convertMarkToStyle } from '../../../../../../common/tiptap/conversion/editorConversion/toDraftJs/convertTiptapMarksToInlineStyleRanges';
import { reduceTiptapContent } from '../../../../../../common/tiptap/utils/jsonContentUtils/reduceTiptapContent';
import { getBackgroundColorClasses } from '../../../../utils/elementColorStyleUtils';

/**
 * Returns the rendered background color for a cell.
 */
export const getCellBackgroundColor = (cellData?: CellData): string => {
    const { background = null } = cellData || {};
    const colorValue = getColorDisplayValue(background);

    if (colorValue && colorValue.match(/^#([0-9a-f]{3}){1,2}$/i)) return colorValue;
    if (background === TABLE_DEFAULT_HEADER_BACKGROUND_COLOR) return 'var(--ws-table-header-row-background)';

    return 'var(--ws-element-background-primary)';
};

/**
 * Returns the background class flags for a cell renderer.
 */
export const getBackgroundColorClass = (cellData?: CellData): Record<string, boolean> | undefined => {
    const { background = null } = cellData || {};
    if (!background) return;
    if (background === TABLE_DEFAULT_HEADER_BACKGROUND_COLOR) return { 'colored-background': true };

    return getBackgroundColorClasses(background);
};

/**
 * Resolves a table background token to a concrete hex color.
 */
export const getBackgroundColorHex = (color: string | null | undefined, isDarkMode: boolean): string => {
    if (!color) return isDarkMode ? '#333333' : '#ffffff';
    if (color === TABLE_DEFAULT_HEADER_BACKGROUND_COLOR) return isDarkMode ? '#4D4D4D' : '#D2D3D6';

    // @ts-ignore - if it's not a valid key then we just return the color value
    return TABLE_DEFAULT_COLORS[color]?.hex || color;
};

const getSharedTextStyles = (sharedStyles: Set<string> | undefined, textStyles: Set<string>): Set<string> => {
    if (!sharedStyles) return textStyles;

    return new Set([...sharedStyles].filter((style) => textStyles.has(style)));
};

/**
 * Returns the Tiptap styles that apply uniformly across all text content in the cell.
 */
export const getTiptapCellLevelTextStyles = (content: TiptapContent): string[] => {
    const sharedStyles = reduceTiptapContent<Set<string> | undefined>(
        content,
        (currentSharedStyles, node) => {
            if (node.type !== TiptapNodeType.text || !node.text) return currentSharedStyles;

            const textStyles = new Set(node.marks?.map(convertMarkToStyle).filter(Boolean) as string[]);
            return getSharedTextStyles(currentSharedStyles, textStyles);
        },
        undefined,
    );

    return [...(sharedStyles || [])];
};
